import { useEffect, useRef, useState } from 'react';
import { MISSED_CUE_GRACE_MS, cueDone, cuePhase, tick as tickCue, unlockAudio } from '../lib/audio';
import { useBackupPush } from '../lib/useBackupPush';
import { useWakeLock } from '../lib/useWakeLock';

// Both timers here are deadline-based: they store the wall-clock moment the
// current phase ends and derive the display from Date.now(). They used to
// decrement a counter once per setInterval callback, which quietly lost time
// whenever iOS throttled or suspended the page — lock the screen mid-set and
// the countdown simply froze, then resumed from where it stopped. Reading the
// clock instead means a suspended timer is still *correct* when you come back;
// it just wasn't animating while you weren't looking.
//
// useWakeLock is the other half: while a timer runs we ask the phone not to
// dim and lock in the first place, so it never gets suspended. That request can
// be refused (Low Power Mode, Safari < 16.4), which is exactly why the clock
// math above has to stand on its own.

const TICK_MS = 250;

// Blip over the final seconds of a phase so you can look up from the bar.
const COUNTDOWN_FROM_SEC = 3;

function fmt(sec: number): string {
  const safe = Math.max(0, Math.floor(sec));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

type HoldState =
  | { kind: 'idle' }
  | { kind: 'running'; endsAt: number }
  | { kind: 'paused'; remainingMs: number }
  | { kind: 'done' };

export function HoldTimer({
  seconds,
  label,
  exerciseName,
}: {
  seconds: number;
  label?: string;
  exerciseName?: string;
}) {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const [state, setState] = useState<HoldState>({ kind: 'idle' });
  const [, forceRender] = useState(0);
  const lastBlipRef = useRef(-1);
  // The deadline we've already resolved. Expiry is reachable from two places at
  // once — the interval and the visibilitychange handler — and the interval
  // keeps firing until React tears it down on the next render. Without this the
  // chirp can land several times for one finished timer.
  const resolvedRef = useRef(0);

  useWakeLock(state.kind === 'running');
  useBackupPush(
    state.kind === 'running' ? state.endsAt : null,
    'Timer done',
    exerciseName ?? 'Hold complete',
  );

  // A different prescription (new exercise row) resets the timer.
  useEffect(() => {
    setState({ kind: 'idle' });
    lastBlipRef.current = -1;
  }, [safeSeconds]);

  useEffect(() => {
    if (state.kind !== 'running') return;
    const { endsAt } = state;

    // resolve is the only place the deadline meets the clock. It runs on the
    // interval and again on visibilitychange, so coming back to a suspended
    // page settles the timer at once instead of up to TICK_MS later.
    const resolve = () => {
      const now = Date.now();
      const remainingMs = endsAt - now;
      if (remainingMs > 0) {
        const secs = Math.ceil(remainingMs / 1000);
        if (secs <= COUNTDOWN_FROM_SEC && secs !== lastBlipRef.current) {
          lastBlipRef.current = secs;
          tickCue();
        }
        forceRender((n) => n + 1);
        return;
      }
      if (resolvedRef.current === endsAt) return;
      resolvedRef.current = endsAt;
      if (now - endsAt <= MISSED_CUE_GRACE_MS) cueDone();
      setState({ kind: 'done' });
    };

    const id = window.setInterval(resolve, TICK_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') resolve();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [state]);

  const toggle = () => {
    // Must happen inside the tap: iOS only unlocks audio from a gesture.
    unlockAudio();
    if (state.kind === 'running') {
      setState({ kind: 'paused', remainingMs: Math.max(0, state.endsAt - Date.now()) });
      return;
    }
    lastBlipRef.current = -1;
    if (state.kind === 'paused') {
      setState({ kind: 'running', endsAt: Date.now() + state.remainingMs });
      return;
    }
    setState({ kind: 'running', endsAt: Date.now() + safeSeconds * 1000 });
  };

  const reset = () => {
    lastBlipRef.current = -1;
    setState({ kind: 'idle' });
  };

  if (safeSeconds === 0) return null;

  const remaining =
    state.kind === 'running' ? Math.max(0, Math.ceil((state.endsAt - Date.now()) / 1000))
    : state.kind === 'paused' ? Math.max(0, Math.ceil(state.remainingMs / 1000))
    : state.kind === 'done' ? 0
    : safeSeconds;

  const buttonLabel =
    state.kind === 'running' ? 'Pause'
    : state.kind === 'paused' ? 'Resume'
    : state.kind === 'done' ? 'Restart'
    : 'Start';

  // The readout carries the state: ink while it matters, green once the hold is
  // banked, grey while paused. No decorative colour — this is read mid-set.
  const tone =
    state.kind === 'done' ? 'text-good'
    : state.kind === 'paused' ? 'text-fg-muted'
    : 'text-fg';

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-panel bg-bg-sunken border border-line px-3 py-2.5">
      <span className={`font-display text-2xl leading-none tabular-nums min-w-[4.5rem] ${tone}`}>
        {fmt(remaining)}
      </span>
      <button type="button" onClick={toggle} className="btn-primary text-xs px-4 py-2.5">
        {buttonLabel}
      </button>
      <button type="button" onClick={reset} className="btn-secondary text-xs px-4 py-2.5">
        Reset
      </button>
      {label && <span className="label-eyebrow ml-auto">{label}</span>}
    </div>
  );
}

export type IntervalPhase = {
  mode: 'work' | 'rest';
  round: number;
  remainingMs: number;
  done: boolean;
};

// resolveInterval maps elapsed time onto the work/rest schedule. This is the
// whole correctness story for a timer that can be suspended for minutes and
// must land on the right round when it wakes up, so it's a pure function and
// tested directly.
//
// The schedule is `rounds` repetitions of (work then rest) — the trailing rest
// of the final round is part of the workout, matching the original behaviour.
// A zero rest collapses each cycle to just the work interval.
export function resolveInterval(
  elapsedMs: number,
  workSec: number,
  restSec: number,
  rounds: number,
): IntervalPhase {
  const cycleMs = (workSec + restSec) * 1000;
  const totalMs = cycleMs * rounds;
  if (cycleMs <= 0 || elapsedMs >= totalMs) {
    return { mode: 'rest', round: rounds, remainingMs: 0, done: true };
  }
  const elapsed = Math.max(0, elapsedMs);
  const workMs = workSec * 1000;
  const round = Math.floor(elapsed / cycleMs) + 1;
  const within = elapsed % cycleMs;
  if (within < workMs) {
    return { mode: 'work', round, remainingMs: workMs - within, done: false };
  }
  return { mode: 'rest', round, remainingMs: cycleMs - within, done: false };
}

type IntervalState =
  | { kind: 'idle' }
  // startedAt is pause-adjusted: it's always (now − elapsed), so the schedule
  // can be resolved from the clock alone without tracking accumulated ticks.
  | { kind: 'running'; startedAt: number }
  | { kind: 'paused'; elapsedMs: number }
  | { kind: 'done' };

export function IntervalTimer({
  workSec,
  restSec,
  rounds,
  exerciseName,
}: {
  workSec: number;
  restSec: number;
  rounds: number;
  exerciseName?: string;
}) {
  const safeWork = Math.max(0, Math.floor(workSec));
  const safeRest = Math.max(0, Math.floor(restSec));
  const safeRounds = Math.max(1, Math.floor(rounds));

  const [state, setState] = useState<IntervalState>({ kind: 'idle' });
  const [, forceRender] = useState(0);
  const lastBlipRef = useRef('');
  const lastPhaseRef = useRef('');
  // See HoldTimer: completion is reachable twice over, so latch it.
  const resolvedRef = useRef(0);

  useWakeLock(state.kind === 'running');
  // Only the end of the whole block, not each work→rest flip. Sixteen
  // notifications across a four-minute Tabata would be nonsense, and iOS would
  // coalesce them anyway. Per-phase cues on a locked screen are simply not
  // possible from a PWA — that's what the wake lock is for.
  useBackupPush(
    state.kind === 'running'
      ? state.startedAt + (safeWork + safeRest) * 1000 * safeRounds
      : null,
    'Intervals done',
    exerciseName ?? 'Conditioning complete',
  );

  useEffect(() => {
    setState({ kind: 'idle' });
    lastBlipRef.current = '';
    lastPhaseRef.current = '';
  }, [safeWork, safeRest, safeRounds]);

  useEffect(() => {
    if (state.kind !== 'running') return;
    const { startedAt } = state;
    const totalMs = (safeWork + safeRest) * 1000 * safeRounds;

    const resolve = () => {
      const now = Date.now();
      const phase = resolveInterval(now - startedAt, safeWork, safeRest, safeRounds);

      if (phase.done) {
        if (resolvedRef.current === startedAt) return;
        resolvedRef.current = startedAt;
        // Only cue if it actually just finished — if we were suspended for an
        // hour, skip straight to done without chirping.
        if (now - (startedAt + totalMs) <= MISSED_CUE_GRACE_MS) cueDone();
        setState({ kind: 'done' });
        return;
      }

      // One cue per phase change, even if we slept through several of them.
      const phaseKey = `${phase.round}:${phase.mode}`;
      if (lastPhaseRef.current && lastPhaseRef.current !== phaseKey) cuePhase();
      lastPhaseRef.current = phaseKey;

      const secs = Math.ceil(phase.remainingMs / 1000);
      const blipKey = `${phaseKey}:${secs}`;
      if (secs <= COUNTDOWN_FROM_SEC && blipKey !== lastBlipRef.current) {
        lastBlipRef.current = blipKey;
        tickCue();
      }
      forceRender((n) => n + 1);
    };

    const id = window.setInterval(resolve, TICK_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') resolve();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [state, safeWork, safeRest, safeRounds]);

  const toggle = () => {
    unlockAudio();
    if (state.kind === 'running') {
      setState({ kind: 'paused', elapsedMs: Math.max(0, Date.now() - state.startedAt) });
      return;
    }
    lastBlipRef.current = '';
    if (state.kind === 'paused') {
      // Seed the baseline from where we're resuming, so restarting the clock
      // isn't mistaken for a phase change.
      const p = resolveInterval(state.elapsedMs, safeWork, safeRest, safeRounds);
      lastPhaseRef.current = `${p.round}:${p.mode}`;
      setState({ kind: 'running', startedAt: Date.now() - state.elapsedMs });
      return;
    }
    // Seed the baseline here rather than on the first tick: if the screen locks
    // between Start and that tick, we'd otherwise have no phase to compare
    // against on the way back and would swallow the transition cue.
    lastPhaseRef.current = '1:work';
    setState({ kind: 'running', startedAt: Date.now() });
  };

  const reset = () => {
    lastBlipRef.current = '';
    lastPhaseRef.current = '';
    setState({ kind: 'idle' });
  };

  if (safeWork === 0) return null;

  const elapsedMs =
    state.kind === 'running' ? Date.now() - state.startedAt
    : state.kind === 'paused' ? state.elapsedMs
    : 0;
  const phase = resolveInterval(elapsedMs, safeWork, safeRest, safeRounds);

  const isDone = state.kind === 'done';
  const remaining =
    state.kind === 'idle' ? safeWork
    : isDone ? 0
    : Math.max(0, Math.ceil(phase.remainingMs / 1000));
  const round = state.kind === 'idle' ? 1 : isDone ? safeRounds : phase.round;

  const tone =
    isDone ? 'text-good'
    : state.kind === 'paused' ? 'text-fg-muted'
    : 'text-fg';

  // The phase badge is where work/rest reads at a glance: a solid ink pill while
  // you're working — the same black as every other live thing in the app — a
  // quiet white one while you're resting, green once the block is banked.
  const phaseChip =
    isDone ? 'bg-good-soft text-good'
    : state.kind !== 'running' ? 'text-fg-faint'
    : phase.mode === 'work' ? 'bg-accent text-accent-fg'
    : 'bg-bg-raised text-fg-muted';

  const phaseLabel =
    isDone ? 'done'
    : state.kind === 'idle' ? 'ready'
    : state.kind === 'paused' ? 'paused'
    : phase.mode;

  const buttonLabel =
    isDone ? 'Restart'
    : state.kind === 'running' ? 'Pause'
    : state.kind === 'paused' ? 'Resume'
    : 'Start';

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-panel bg-bg-sunken border border-line px-3 py-2.5">
      <span className={`font-display text-2xl leading-none tabular-nums min-w-[4.5rem] ${tone}`}>{fmt(remaining)}</span>
      <span
        className={`rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.16em] transition-colors ${phaseChip}`}
      >
        {phaseLabel} · r{round}/{safeRounds}
      </span>
      <button type="button" onClick={toggle} className="btn-primary text-xs px-4 py-2.5">
        {buttonLabel}
      </button>
      <button type="button" onClick={reset} className="btn-secondary text-xs px-4 py-2.5">
        Reset
      </button>
    </div>
  );
}
